import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import type { useUpdateSystemConfigMutation } from '@stocker/codegen/configurator-backend'
import type { IImage } from '@stocker/ui-components/design-system'
import { FiRsBan, FiRsCheck, FiRsInterrogation } from '@stocker/ui-components/design-system'
import {
  divideValueDependingOnUnit,
  multiplyValueDependingOnUnit,
} from '@stocker/ui-components/helpers'
import { Field, Form, Formik } from 'formik'
import inRange from 'lodash/inRange'
import type React from 'react'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { InfoPopover } from '../../compounds/InfoPopover/InfoPopover'
import { hiddenFieldIdSet, isFieldHidden, operatorFunctions, parseValueBasedOnStringPure } from './hideConditions'
import { EW06_SYSTEM_CODE, KOMBILINE_LBS_SYSTEM_CODE } from '../SystemSummary/drawingRules'

interface ITooltip {
  title: string
  description: string
  image: IImage
}

interface systemModuleItem {
  id: string
  name: string
  minValue: string
  maxValue: string
  valueType: 'NUMBER' | 'SELECT' | 'BOOL' | 'DYNAMIC_SELECT'
  selectValues?: string[]
  /**
   * When provided and same length as `selectValues`, the SELECT renders
   * `selectValues[i]` as the visible label and submits/stores `optionKeys[i]` as the value.
   * Used for Pimcore key→value option tables (e.g. KombiLine Systemauswahl labels → sub-system codes).
   */
  optionKeys?: string[]
  unit?: string
  tooltip?: ITooltip
  isMandatory?: boolean
  defaultValue?: string
  hideConditions?: Array<{
    targetFieldId: string
    operator: string
    targetFieldValue: string
    disableIfHidden?: boolean
    condition?: string
  }>
}

export interface ISystemModuleConfiguratorProps {
  isDisabled: boolean
  accordionItems: Array<{
    title: string
    isSkippable?: boolean
    tooltip?: ITooltip
    fields?: systemModuleItem[]
    /** When set, a "ZUG {n}" supermodule header is rendered above this item. */
    zugSectionStart?: 1 | 2
  }>
  maxClickableModuleIndex: number
  systemConfigId: string
  updateConfigMutation: ReturnType<typeof useUpdateSystemConfigMutation>
  currentConfigValues: Array<{ fieldId: string; value: string }>
  currentConfigName: string
  systemCode?: string
}

interface IModuleFieldValue {
  fieldId: string
  value: string
}

/**
 * Field id of the legacy "composite" field whose 3 hideConditions encode the
 * non-OR pattern `cond1 OR (cond2 AND cond3)` (see `evaluateSpecialHideConditions`).
 * Survives ZUG cloning: per-ZUG clones append `__zug{n}` to the id, so the
 * compare strips the suffix before matching.
 *
 * NOTE: This is a one-off Pimcore-data quirk. Long term the rule should be
 * encoded in `condition`/`operator` data, not by hardcoding a field id.
 */
const SPECIAL_COMPOSITE_FIELD_ID = 'c7d31596-ab3e-5286-9e13-24a14a81e24c'

function stripZugSuffix(fieldId: string): string {
  return fieldId.replace(/__zug\d+$/, '')
}

function parseFieldValue(value: string, unit: string) {
  switch (value) {
    case 'true':
      return true
    case 'false':
      return false
    default:
      return divideValueDependingOnUnit(value, unit)
  }
}

function getModuleIcon(
  isExpanded: boolean,
  item: ISystemModuleConfiguratorProps['accordionItems'][0],
  index: number,
  openAccordionIndex: number,
  currentFieldIds?: string[],
) {
  if (isExpanded && item.tooltip) {
    return <InfoPopover {...item.tooltip} popOverTrigger={<FiRsInterrogation fontSize="xl" />} />
  }

  // Show icons for completed modules (when accordion is closed or when index < openAccordionIndex)
  // openAccordionIndex === -1 means all modules are complete, so show icons for all modules with values
  const shouldShowCompletionIcon = openAccordionIndex === -1
    ? currentFieldIds && currentFieldIds.length > 0 && item.fields?.some((field) => currentFieldIds.includes(field.id))
    : index < openAccordionIndex

  if (shouldShowCompletionIcon) {
    // if no fields of a module are on the submitted values also show the skipped icon (if all inputs are skippable)
    const moduleValuesEmpty =
      item.fields?.filter((field) => currentFieldIds?.includes(field.id)).length === 0
    if (moduleValuesEmpty) {
      return <FiRsBan fontSize="xl" />
    } else {
      return <FiRsCheck fontSize="xl" />
    }
  }
}

function asModuleFieldValueArray(values: Record<string, any>): IModuleFieldValue[] {
  return Object.keys(values).map((key) => {
    return {
      fieldId: key,
      value: String(values[key]),
    }
  })
}

function asModuleFieldValueObject(values: IModuleFieldValue[]): Record<string, string> {
  const obj: Record<string, string> = {}
  values.forEach((value) => {
    obj[value.fieldId] = value.value
  })
  return obj
}

function addMissingCheckboxFalseValues(
  values: Record<string, any>,
  checkboxFields: systemModuleItem[],
) {
  const newValues = structuredClone(values)
  for (const checkboxField of checkboxFields) {
    const checkboxIsNotIncluded = !Object.keys(values).includes(checkboxField.id)
    if (checkboxIsNotIncluded) {
      newValues[checkboxField.id] = 'false'
    }
  }
  return newValues
}

function correctUnits(
  values: IModuleFieldValue[],
  numberFieldsWithDifferentUnit: systemModuleItem[],
) {
  return values.map((value) => {
    const fieldWithChangedUnit = numberFieldsWithDifferentUnit.find((e) => e.id === value.fieldId)

    if (fieldWithChangedUnit) {
      return {
        fieldId: fieldWithChangedUnit.id,
        value: String(multiplyValueDependingOnUnit(value.value, fieldWithChangedUnit.unit ?? '')),
      }
    }
    return value
  })
}

// function hasEditableFields (item: ISystemModuleConfiguratorProps['accordionItems'][0], hiddenFieldIds?: string[]) {
//   return (item.fields?.filter(field => !hiddenFieldIds?.includes(field.id)).length ?? 0) > 0
// }

export const SystemModuleConfigurator: React.FC<ISystemModuleConfiguratorProps> = ({
  currentConfigName,
  systemCode,
  currentConfigValues,
  updateConfigMutation,
  systemConfigId,
  accordionItems,
  maxClickableModuleIndex,
  isDisabled,
}) => {
  const [openAccordionIndex, setOpenAccordionIndex] = useState<number>(0)
  const [isAutoSaving, setIsAutoSaving] = useState<boolean>(false)
  const { formatMessage } = useIntl()
  const toast = useToast()

  // Single source for "a save is in flight". The mutation is shared with the page, so this also
  // covers the auto-save below, which runs outside Formik and never sets `isSubmitting`.
  const isSaving = updateConfigMutation.isLoading

  // Keep track of initial state to prevent auto-progression
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false)

  // Keep track of whether we're in the middle of a manual navigation
  const [isManualNavigation, setIsManualNavigation] = useState<boolean>(false)

  // Track the last manually set accordion index
  const [lastManualIndex, setLastManualIndex] = useState<number | null>(null)

  // Open the last open module on page reload, but NEVER if we've manually navigated
  useEffect(() => {
    // IMPORTANT: Never update accordion index during manual navigation, auto-saving, or after manual navigation
    if (isManualNavigation || isAutoSaving || lastManualIndex !== null) {
      return
    }

    // IMPORTANT: If accordion is closed (-1) AND lastManualIndex is -1, it means configuration was auto-completed, don't reopen it
    // BUT if lastManualIndex is NOT -1, user manually opened a module, so allow it
    if (openAccordionIndex === -1 && lastManualIndex === -1) {
      return
    }

    // Only auto-progress if we haven't just manually navigated
    if (hasUserInteracted && openAccordionIndex <= maxClickableModuleIndex) {
      setOpenAccordionIndex(maxClickableModuleIndex)
    }
  }, [maxClickableModuleIndex, isAutoSaving, hasUserInteracted, isManualNavigation, openAccordionIndex, lastManualIndex])

  const formInitialValues: Record<string, any> = {}
  for (const configValue of currentConfigValues) {
    const valueModule = accordionItems.find((e) =>
      e.fields?.find((e) => e.id === configValue.fieldId),
    )
    const field = valueModule?.fields?.find((e) => e.id === configValue.fieldId)
    const unit = field?.unit
    const valueType = field?.valueType

    // SELECT/DYNAMIC_SELECT fields store values in mm already (e.g., 1000, 2000, 3000)
    // Only the display is converted to m - so we should NOT divide the value when loading
    // Only NUMBER fields should have their values divided for display
    if (valueType === 'SELECT' || valueType === 'DYNAMIC_SELECT') {
      formInitialValues[configValue.fieldId] = configValue.value
    } else {
      formInitialValues[configValue.fieldId] = parseFieldValue(configValue.value, unit ?? '')
    }
  }

  // Auto-select single option SELECT fields and update config values
  const singleOptionCandidates: Array<{ fieldId: string; value: string }> = []
  accordionItems.forEach((module) => {
    module.fields?.forEach((field) => {
      if ((field.valueType === 'SELECT' || field.valueType === 'DYNAMIC_SELECT') &&
          field.selectValues?.length === 1) {
        // Check if value is not already set
        const existingValue = currentConfigValues.find(v => v.fieldId === field.id)
        // When optionKeys is provided, the submitted/stored value is the key, not the visible label.
        const singleSubmitValue = field.optionKeys?.[0] ?? field.selectValues[0]
        if (!existingValue && !field.defaultValue) {
          formInitialValues[field.id] = singleSubmitValue
          singleOptionCandidates.push({ fieldId: field.id, value: singleSubmitValue })
        } else if (!existingValue && field.defaultValue) {
          // If there's a default value but single option, ensure it matches
          formInitialValues[field.id] = singleSubmitValue
        }
      }
    })
  })

  // Auto-save single option values if there are any
  useEffect(() => {
    // STO-80, STO-84: EW06 and LBS neither store the single option of a field the form hides nor
    // keep a value stored for one. Configurations saved before carry such values and get cleaned, and
    // so recalculated, once on load, except while locked by an expert check: opening one must not
    // change a Stückliste after approval. Other systems are unchanged.
    const cleansHidden = systemCode === EW06_SYSTEM_CODE || systemCode === KOMBILINE_LBS_SYSTEM_CODE
    const hiddenOnLoad = cleansHidden && !isDisabled
      ? hiddenFieldIdSet(accordionItems.flatMap((m) => m.fields ?? []), formInitialValues)
      : new Set<string>()
    const singleOptionUpdates = singleOptionCandidates.filter((u) => !hiddenOnLoad.has(u.fieldId))
    const hasStoredHiddenValues = currentConfigValues.some((v) => hiddenOnLoad.has(v.fieldId))

    if ((singleOptionUpdates.length > 0 || hasStoredHiddenValues) && updateConfigMutation) {
      // Check if these values are not already in currentConfigValues to avoid re-saving
      const needsUpdate = hasStoredHiddenValues || singleOptionUpdates.some(update =>
        !currentConfigValues.find(v => v.fieldId === update.fieldId && v.value === update.value)
      )

      if (needsUpdate) {
        // Set flag to prevent accordion from jumping
        setIsAutoSaving(true)

        const updatedValues = [
          ...currentConfigValues.filter((v) => !hiddenOnLoad.has(v.fieldId)),
          ...singleOptionUpdates
        ]
        updateConfigMutation.mutate({
          config: {
            systemConfigId,
            name: currentConfigName,
            moduleFieldValues: updatedValues,
          },
        })

        // Reset flag after a short delay
        setTimeout(() => {
          setIsAutoSaving(false)
        }, 500)
      }
    }
  }, [systemConfigId]) // Re-run when system config changes

  const submittedFieldIds = currentConfigValues.map((value) => value.fieldId)

  const clickableAccordionIndexes = accordionItems
    .map((accordionItem, index) => {
      const fieldHasBeenTouched =
        accordionItem.fields?.filter((field) => submittedFieldIds.includes(field.id)).length !== 0
      // When configuration is complete (openAccordionIndex === -1), all modules should be clickable for editing
      if (fieldHasBeenTouched || index <= openAccordionIndex || openAccordionIndex === -1) {
        return index
      }
    })
    .filter((e) => e !== undefined)

  const [hiddenFieldIds, setHiddenFieldIds] = useState<string[]>()
  const [selectedDNFieldValue, setSelectedDNFieldValue] = useState<string>()
  const [selectedPressureFieldValue, setSelectedPressureFieldValue] = useState<string>()

  // Use useMemo to ensure fieldsWithChangedUnit is recalculated when accordionItems changes
  // This includes ALL number fields with non-mm units across ALL modules
  const fieldsWithChangedUnit = useMemo(() => {
    const fields: systemModuleItem[] = []
    accordionItems.forEach((module) => {
      module.fields?.forEach((field) => {
        if (field.valueType === 'NUMBER' && field.unit && field.unit !== 'mm') {
          fields.push(field)
        }
      })
    })
    return fields
  }, [accordionItems])

  // Use refs to queue state updates during render
  const pendingHiddenFieldUpdates = useRef<{ add: string[], remove: string[] }>({ add: [], remove: [] })

  // Apply pending updates after render
  useEffect(() => {
    const { add, remove } = pendingHiddenFieldUpdates.current

    if (add.length > 0 || remove.length > 0) {
      setHiddenFieldIds(prev => {
        let newIds = prev ? [...prev] : []
        // Remove fields
        if (remove.length > 0) {
          newIds = newIds.filter(id => !remove.includes(id))
        }
        // Add fields
        add.forEach(id => {
          if (!newIds.includes(id)) {
            newIds.push(id)
          }
        })
        return newIds.length > 0 ? newIds : undefined
      })
      pendingHiddenFieldUpdates.current = { add: [], remove: [] }
    }
  })

  const allFields = accordionItems
    .map((module) => {
      return module.fields
    })
    .flat()

  // Alias the module-level helper so existing call sites inside the component body don't need to change.
  const parseValueBasedOnString = parseValueBasedOnStringPure

  return (
    // Chakra v2's Accordion tracks AccordionItem children via the descendants
    // context (not cloneElement), so a plain <Box> sibling for the ZUG header
    // renders harmlessly and does not interfere with item registration.
    <Accordion defaultIndex={maxClickableModuleIndex} index={isDisabled ? -1 : openAccordionIndex}>
      {accordionItems
        .map((item, index) => (
          <Fragment key={item.title + String(index)}>
            {item.zugSectionStart !== undefined && (
              <Box
                bg="red.600"
                color="white"
                px={4}
                py={3}
                fontSize="lg"
                fontWeight="bold"
                mt={index === 0 ? 0 : 2}
              >
                {formatMessage({ id: 'configurator-zug--header' }, { number: item.zugSectionStart })}
              </Box>
            )}
          <AccordionItem
            // Block header navigation while a save/recalculation is in flight so the user
            // can't jump to another module mid-save and have onSuccess yank them back (STO-14).
            isDisabled={isDisabled || isSaving || !clickableAccordionIndexes.includes(index)}
          >
            {({ isExpanded }) => (
              <>
                <AccordionButton
                  onClick={() => {
                    const isClickable = clickableAccordionIndexes.includes(index)
                    if (isClickable) {
                      // Set lastManualIndex to current index to prevent useEffect from interfering
                      setLastManualIndex(index)
                      setOpenAccordionIndex(index)
                    }
                  }}
                  _expanded={{ bg: 'black', color: 'white' }}
                  borderLeft="1px"
                  borderRight="1px"
                  borderColor={isExpanded ? 'black' : 'gray.300'}
                >
                  <Box fontSize="lg" fontWeight="bold" as="span" flex="1" textAlign="left">
                    {item.title}
                  </Box>
                  {getModuleIcon(isExpanded, item, index, openAccordionIndex, submittedFieldIds)}
                </AccordionButton>
                <AccordionPanel pb={4} border="1px" borderColor="black">
                  <Formik
                    initialValues={formInitialValues}
                    enableReinitialize={true}
                    validate={(values) => {
                      const errors: Record<string, string> = {}
                      // Block submit if any VISIBLE mandatory field is empty.
                      // Checkboxes are excluded — missing checkbox is a valid "false".
                      const moduleFieldList = item.fields ?? []
                      for (const f of moduleFieldList) {
                        if (!f.isMandatory) continue
                        if (f.valueType === 'BOOL') continue
                        if (isFieldHidden(f, values, allFields)) continue
                        const v = values[f.id]
                        if (v === undefined || v === '' || v === null) {
                          errors[f.id] = formatMessage({ id: 'configurator-validation--required', defaultMessage: 'Benötigte Angabe' })
                        }
                      }
                      return errors
                    }}
                    onSubmit={(values, actions) => {
                      // Mark that user has interacted (manually submitted)
                      setHasUserInteracted(true)

                      const checkboxFields =
                        item.fields?.filter((field) => field.valueType === 'BOOL') ?? []
                      const newValues = addMissingCheckboxFalseValues(values, checkboxFields)

                      const joinedValues = asModuleFieldValueArray({
                        ...asModuleFieldValueObject(currentConfigValues),
                        ...newValues,
                      })

                      const withCorrectUnit = correctUnits(
                        joinedValues,
                        fieldsWithChangedUnit ?? [],
                      )

                      // Recalculate hidden fields based on ALL config values (not just current module)
                      const currentlyHiddenFieldIds: string[] = []
                      // Create a map of all values: saved config + current form values
                      const allConfigValues: Record<string, string> = {}
                      for (const cv of joinedValues) {
                        allConfigValues[cv.fieldId] = cv.value
                      }

                      // Reuse the module-level `isFieldHidden` against `allConfigValues` (saved + form).
                      allFields.forEach((field) => {
                        if (field?.hideConditions && isFieldHidden(field, allConfigValues, allFields)) {
                          currentlyHiddenFieldIds.push(field.id)
                        }
                      })

                      // Drop values for fields no longer present in the effective module set
                      // (e.g. ZUG2 values left in DB after user switched Zweizügig → Einzügig).
                      // Note: when the General module itself is the one switching Ausführung, the
                      // `accordionItems` prop is still the previous-render shape, so ZUG2 entries
                      // get one extra re-save before being pruned on the next submit. Acceptable —
                      // the stale ZUG2 rows are harmless: calculators read per-ZUG values via
                      // `getFieldForZug(zug)` which is only called for active ZUGs.
                      const allFieldIds = new Set(allFields.map((f) => f?.id).filter(Boolean) as string[])
                      const withoutHiddenFields = withCorrectUnit.filter(
                        (e) => !currentlyHiddenFieldIds.includes(e.fieldId) && allFieldIds.has(e.fieldId),
                      )

                      // Final "Berechnen" click: globally validate every visible required field
                      // across ALL modules. If something's missing, jump the user to the first
                      // module with the gap and abort the calculation.
                      const isFinalClick = index === accordionItems.length - 1
                      if (isFinalClick) {
                        const valuesByFieldId: Record<string, unknown> = {}
                        for (const v of withoutHiddenFields) valuesByFieldId[v.fieldId] = v.value

                        let firstMissingModuleIndex: number | undefined
                        for (let modIdx = 0; modIdx < accordionItems.length; modIdx++) {
                          const mod = accordionItems[modIdx]
                          for (const f of mod.fields ?? []) {
                            if (!f.isMandatory) continue
                            if (f.valueType === 'BOOL') continue
                            if (isFieldHidden(f, valuesByFieldId, allFields)) continue
                            const v = valuesByFieldId[f.id]
                            if (v === undefined || v === '' || v === null) {
                              firstMissingModuleIndex = modIdx
                              break
                            }
                          }
                          if (firstMissingModuleIndex !== undefined) break
                        }

                        if (firstMissingModuleIndex !== undefined) {
                          setIsManualNavigation(true)
                          setLastManualIndex(firstMissingModuleIndex)
                          setOpenAccordionIndex(firstMissingModuleIndex)
                          toast({
                            title: formatMessage({ id: '--error' }),
                            description: formatMessage({
                              id: 'configurator-validation--fill-required-fields',
                              defaultMessage: 'Bitte füllen Sie alle benötigten Angaben aus.',
                            }),
                            status: 'error',
                            duration: 5000,
                            isClosable: true,
                          })
                          actions.setSubmitting(false)
                          return
                        }
                      }

                      // Keep the form "submitting" (loader overlay + disabled inputs) until the
                      // save/recalculation resolves — the mutate() below advances only on success.
                      // Set manual navigation flag to prevent useEffect from interfering
                      setIsManualNavigation(true)

                      // Find the next incomplete module instead of just going to next index
                      const findNextIncompleteModule = () => {
                        // Get all current config values including the ones we just submitted AND single-option auto-filled values
                        const allCurrentValues = [...withoutHiddenFields]

                        // Also include single-option values that are auto-filled
                        accordionItems.forEach((module) => {
                          module.fields?.forEach((field) => {
                            if ((field.valueType === 'SELECT' || field.valueType === 'DYNAMIC_SELECT') &&
                                field.selectValues?.length === 1) {
                              // Check if this auto-filled value isn't already in allCurrentValues
                              const existingValue = allCurrentValues.find(v => v.fieldId === field.id)
                              if (!existingValue) {
                                const submitValue = field.optionKeys?.[0] ?? field.selectValues[0]
                                allCurrentValues.push({ fieldId: field.id, value: submitValue })
                              }
                            }
                          })
                        })

                        // Check all modules (including current and previous ones) to see if configuration is complete
                        let allModulesComplete = true
                        for (let i = 0; i < accordionItems.length; i++) {
                          const module = accordionItems[i]

                          // Get all visible (non-hidden) fields in this module
                          // Use currentlyHiddenFieldIds (freshly calculated) instead of hiddenFieldIds state
                          const visibleFields = module.fields?.filter(field =>
                            !currentlyHiddenFieldIds?.includes(field.id)
                          ) || []

                          // If module has no visible fields, it's considered complete (skip it)
                          if (visibleFields.length === 0) {
                            continue
                          }

                          // Check if all visible fields are single-option (auto-filled)
                          // If so, we should still show the module to the user but not require interaction
                          const allFieldsAreSingleOption = visibleFields.every(field =>
                            (field.valueType === 'SELECT' || field.valueType === 'DYNAMIC_SELECT') && field.selectValues?.length === 1
                          )

                          // All visible fields must have a value (including checkboxes)
                          // For single-option fields, consider them as having values
                          const fieldsWithValues = visibleFields.filter(field => {
                            const hasValue = allCurrentValues.some(configValue => configValue.fieldId === field.id)
                            const hasDefaultValue = field.defaultValue !== undefined && field.defaultValue !== ''
                            const isSingleOption = (field.valueType === 'SELECT' || field.valueType === 'DYNAMIC_SELECT') && field.selectValues?.length === 1
                            return hasValue || hasDefaultValue || isSingleOption
                          })

                          // Module is complete if all fields have values
                          // BUT if all fields are single-option and we haven't visited this module yet,
                          // we should still stop here to show the user what's auto-configured
                          const isModuleComplete = fieldsWithValues.length === visibleFields.length

                          // If module only has single-option fields and hasn't been submitted yet, don't skip it
                          const hasBeenSubmitted = visibleFields.some(field =>
                            allCurrentValues.some(v => v.fieldId === field.id)
                          )
                          const shouldStopAtModule = allFieldsAreSingleOption && !hasBeenSubmitted && i > openAccordionIndex

                          // If this module is after current index and either:
                          // 1. incomplete (and not skippable), OR
                          // 2. has only single-option fields that haven't been submitted yet
                          // then return its index to stop here
                          if (i > openAccordionIndex && ((!isModuleComplete && !module.isSkippable) || shouldStopAtModule)) {
                            return i
                          }

                          // Track if any module is incomplete (only non-skippable modules matter)
                          if (!isModuleComplete && !module.isSkippable) {
                            allModulesComplete = false
                          }
                        }

                        // If all modules are complete, return -1 to close accordion
                        if (allModulesComplete) {
                          return -1
                        }

                        // Otherwise go to next index
                        const nextIndex = Math.min(openAccordionIndex + 1, accordionItems.length - 1)
                        return nextIndex
                      }

                      // Advance to the next module ONLY after the save/recalculation resolves.
                      // Advancing eagerly let the user start filling the next module while the
                      // previous save was still in flight; when it resolved it overwrote those
                      // freshly-entered values and jumped the user back (STO-14).
                      const advanceAfterSave = () => {
                        const nextIndex = findNextIncompleteModule()
                        setOpenAccordionIndex(nextIndex)

                        if (nextIndex === -1) {
                          setLastManualIndex(-1) // Special value indicating configuration is complete
                          setIsManualNavigation(false)
                        } else {
                          setLastManualIndex(nextIndex)
                          // Clear the manual-navigation guard shortly after navigating.
                          setTimeout(() => {
                            setIsManualNavigation(false)
                          }, 3000)
                        }
                      }

                      // NOTE: this onSubmit is intentionally synchronous (returns undefined) so Formik
                      // keeps `isSubmitting` true — which drives the loader overlay — until we call
                      // setSubmitting(false) in onSettled. Do NOT make it async / return the mutation
                      // promise, or the overlay gating breaks.
                      // `updateConfigMutation` is a shared hook owned by the parent. If this subtree
                      // unmounts mid-flight (e.g. an Einzügig↔Zweizügig switch remounts the modules)
                      // these per-call callbacks are dropped; the parent's hook-level onSuccess/onError
                      // still run, so navigation is simply skipped — safe, because the remount
                      // re-derives state from the server.
                      updateConfigMutation.mutate(
                        {
                          config: {
                            systemConfigId,
                            name: currentConfigName,
                            moduleFieldValues: withoutHiddenFields,
                          },
                        },
                        {
                          onSuccess: (data) => {
                            const typename = data.updateSystemConfig.__typename
                            const saved =
                              typename !== 'ConfigNotFoundError' && typename !== 'SystemNotFoundError'
                            if (saved) {
                              advanceAfterSave()
                            } else {
                              // Business error (the parent toasts it) — stay on this module and
                              // release the navigation guard so it isn't stuck.
                              setIsManualNavigation(false)
                            }
                          },
                          onError: () => {
                            // Network/thrown error (the parent toasts it) — stay on this module and
                            // release the navigation guard.
                            setIsManualNavigation(false)
                          },
                          onSettled: () => {
                            actions.setSubmitting(false)
                          },
                        },
                      )
                    }}
                  >
                    {(props) => {
                      // Covers the save itself. The refetch that `onSuccess` starts afterwards is
                      // NOT covered and can still reset the form through `enableReinitialize` (STO-49).
                      const isBusy = props.isSubmitting || isSaving
                      return (
                      <Form>
                        <Box position="relative">
                          {isBusy && (
                            <Center position="absolute" inset={0} zIndex={2} bg="whiteAlpha.700" borderRadius="md">
                              <Spinner size="lg" color="accent.500" thickness="3px" />
                            </Center>
                          )}
                          <Stack
                            spacing={3}
                            py={2}
                            pointerEvents={isBusy ? 'none' : undefined}
                          >
                          {item.fields?.map((fieldValues, index) => {
                            let removeIfDisabled = false

                            // Special-cased composite (preserved from legacy behaviour) takes precedence when present.
                            // Otherwise use the module-level `isFieldHidden` (recursive, off form values).
                            // Strip __zug suffix so per-ZUG clones still trigger the special case.
                            const isFieldDisabled = fieldValues.hideConditions
                              ? stripZugSuffix(fieldValues.id) === SPECIAL_COMPOSITE_FIELD_ID
                                ? evaluateSpecialHideConditions(fieldValues.hideConditions)
                                : isFieldHidden(fieldValues, props.values, allFields)
                              : false

                            // removeIfDisabled: drop field from DOM unless any condition explicitly opts into
                            // "disable, don't remove" via disableIfHidden=true.
                            if (isFieldDisabled && fieldValues.hideConditions && fieldValues.hideConditions.length > 0) {
                              const anyDisableIfHidden = fieldValues.hideConditions.some((c) => c.disableIfHidden)
                              removeIfDisabled = !anyDisableIfHidden
                            }

                            // Only used for SPECIAL_COMPOSITE_FIELD_ID (see top of file).
                            // Encodes the boolean rule `cond1 OR (cond2 AND cond3)` exactly when there are 3 conditions.
                            // Any other condition count → fall back to plain OR semantics.
                            function evaluateSpecialHideConditions(
                              conditions: Array<{
                                targetFieldId: string
                                operator: string
                                targetFieldValue: string
                                disableIfHidden?: boolean
                                condition?: string
                              }>,
                            ): boolean {
                              if (conditions.length !== 3) {
                                return conditions.some(evaluateHideCondition)
                              }

                              const [condition1, condition2, condition3] = conditions

                              if (evaluateHideCondition(condition1)) {
                                return true
                              }

                              return evaluateHideCondition(condition2) && evaluateHideCondition(condition3)
                            }

                            function evaluateHideCondition(condition: {
                              targetFieldId: string
                              operator: string
                              targetFieldValue: string
                              disableIfHidden?: boolean
                              condition?: string
                            }): boolean {
                              const { operator, targetFieldValue, targetFieldId, disableIfHidden } =
                                condition
                              removeIfDisabled = !disableIfHidden

                              // If target field is hidden, skip this condition
                              // (overall cascading hide is handled by allTargetsHidden above)
                              if (hiddenFieldIds?.includes(targetFieldId)) {
                                return false
                              }

                              const relatedField = allFields.find(
                                (field) => field?.id === targetFieldId,
                              )

                              if (relatedField) {
                                const formEntry = Object.entries(props.values).find(
                                  (e) => e[0] === relatedField.id,
                                )

                                // For checkboxes: treat missing value as 'false'
                                if (relatedField.valueType === 'BOOL') {
                                  const checkboxValue = formEntry ? String(formEntry[1]) : 'false'
                                  const hideField = operatorFunctions[
                                    operator as keyof typeof operatorFunctions
                                  ](
                                    parseValueBasedOnString(checkboxValue),
                                    parseValueBasedOnString(targetFieldValue),
                                  )
                                  return hideField
                                }

                                // For SELECT/NUMBER fields: evaluate with empty string if no value
                                const value = formEntry ? String(formEntry[1] ?? '') : ''
                                const hideField = operatorFunctions[
                                  operator as keyof typeof operatorFunctions
                                ](
                                  parseValueBasedOnString(value),
                                  parseValueBasedOnString(targetFieldValue),
                                )

                                if (hideField) return true
                              }
                              return targetFieldValue === 'false' &&
                                !Object.keys(props.values).includes(targetFieldId)
                            }

                            // Queue state updates instead of calling setState during render
                            if (!isFieldDisabled && hiddenFieldIds?.includes(fieldValues.id)) {
                              if (!pendingHiddenFieldUpdates.current.remove.includes(fieldValues.id)) {
                                pendingHiddenFieldUpdates.current.remove.push(fieldValues.id)
                              }
                            }

                            if (isFieldDisabled && !hiddenFieldIds?.includes(fieldValues.id)) {
                              if (!pendingHiddenFieldUpdates.current.add.includes(fieldValues.id)) {
                                pendingHiddenFieldUpdates.current.add.push(fieldValues.id)
                              }
                            }

                            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                            if (isFieldDisabled && removeIfDisabled) return undefined

                            const numberField = (
                              <Field
                                id={fieldValues.name + fieldValues.id}
                                name={fieldValues.id}
                                {...(fieldValues.minValue &&
                                  fieldValues.maxValue && {
                                    validate: (value: string) => {
                                      return !inRange(
                                        Number(value),
                                        Number(fieldValues.minValue),
                                        Number(fieldValues.maxValue) + 1,
                                      )
                                        ? `${formatMessage({
                                            id: '--entered-number-is-invalid',
                                          })} ${fieldValues.minValue} - ${fieldValues.maxValue} ${
                                            fieldValues.unit ?? ''
                                          }`
                                        : null
                                    },
                                  })}
                              >
                                {({ field, form }: { field: any; form: any }) => {
                                  return (
                                    <FormControl
                                      isRequired={fieldValues.isMandatory}
                                      isDisabled={isFieldDisabled}
                                      tabIndex={0}
                                      isInvalid={!!form.errors[field.name]}
                                    >
                                      <Flex justifyContent="space-between" alignItems="center">
                                        <FormLabel mb={1}>
                                          {fieldValues.name}
                                          {fieldValues.unit ? ` (in ${fieldValues.unit})` : ''}
                                        </FormLabel>
                                        {fieldValues.tooltip && (
                                          <Box mb={1} cursor="pointer">
                                            <InfoPopover
                                              {...fieldValues.tooltip}
                                              hideCloseButton={false}
                                              popOverTrigger={<FiRsInterrogation fontSize="sm" />}
                                            />
                                          </Box>
                                        )}
                                      </Flex>
                                      <Input
                                        rounded={0}
                                        type="number"
                                        min={fieldValues.minValue}
                                        max={fieldValues.maxValue}
                                        step={0.01}
                                        focusBorderColor="black"
                                        {...field}
                                        value={field.value ?? fieldValues.defaultValue}
                                      />
                                      {form.errors[field.name] && (
                                        <FormErrorMessage>
                                          {form.errors[field.name]?.toString()}
                                        </FormErrorMessage>
                                      )}
                                    </FormControl>
                                  )
                                }}
                              </Field>
                            )
                            const checkBoxField = (
                              <Field
                                key={fieldValues.name + fieldValues.id}
                                id={fieldValues.id}
                                name={fieldValues.id}
                              >
                                {({ field, form }: { field: any; form: any }) => {
                                  return (
                                    <FormControl
                                      isRequired={fieldValues.isMandatory}
                                      isDisabled={isFieldDisabled}
                                    >
                                      <Flex justifyContent="space-between" alignItems="center">
                                        <Checkbox
                                          my={1}
                                          rounded={0}
                                          isChecked={
                                            field.value ??
                                            ['true', 'wahr'].includes(
                                              fieldValues.defaultValue?.toLowerCase() ?? '',
                                            )
                                          }
                                          {...field}
                                          value={field.value ?? fieldValues.defaultValue}
                                        >
                                          {fieldValues.name}
                                        </Checkbox>
                                        {fieldValues.tooltip && (
                                          <Box cursor="pointer">
                                            <InfoPopover
                                              {...fieldValues.tooltip}
                                              hideCloseButton={false}
                                              popOverTrigger={<FiRsInterrogation fontSize="sm" />}
                                            />
                                          </Box>
                                        )}
                                      </Flex>
                                      {form.errors[field.name] ? (
                                        <FormErrorMessage>
                                          {form.errors[field.name]?.toString()}
                                        </FormErrorMessage>
                                      ) : null}
                                    </FormControl>
                                  )
                                }}
                              </Field>
                            )

                            const selectField = (
                              <Field
                                key={fieldValues.name + fieldValues.id}
                                id={fieldValues.id}
                                name={fieldValues.id}
                              >
                                {({ field, form }: { field: any; form: any }) => {
                                  const isDNField =
                                    fieldValues.id === 'd349d6c2-e3ff-5e95-a927-cbb56c074018'
                                  const isFixationRopeSetField =
                                    fieldValues.id === '8ba41a70-b066-5881-9bc5-713ff44ae5d5' ||
                                    fieldValues.id === '810fae5a-548c-5ff6-9bd3-6ae06b58e8e1'

                                  // Pressure Field with Overpressure, other Pressure Field only allows Underpressure
                                  const isPressureFieldWithOverpressure =
                                    fieldValues.id === '6eb673d1-58d7-5c37-8f80-6266f9f89b37'

                                  // Check if SELECT has only one option
                                  const hasOnlyOneOption = fieldValues.selectValues?.length === 1
                                  // When optionKeys is provided, the submitted value is the key (not the visible label).
                                  const singleOptionValue = hasOnlyOneOption
                                    ? (fieldValues.optionKeys?.[0] ?? (fieldValues.selectValues ? fieldValues.selectValues[0] : null))
                                    : null

                                  return (
                                    <FormControl
                                      isRequired={fieldValues.isMandatory}
                                      isDisabled={isFieldDisabled || hasOnlyOneOption}
                                    >
                                      <Flex justifyContent="space-between" alignItems="center">
                                        <FormLabel mb={1}>
                                          {fieldValues.name}
                                          {fieldValues.unit ? ` (in ${fieldValues.unit})` : ''}
                                        </FormLabel>
                                        {fieldValues.tooltip && (
                                          <Box mb={1} cursor="pointer">
                                            <InfoPopover
                                              {...fieldValues.tooltip}
                                              hideCloseButton={false}
                                              popOverTrigger={<FiRsInterrogation fontSize="sm" />}
                                            />
                                          </Box>
                                        )}
                                      </Flex>
                                      <Select
                                        rounded={0}
                                        placeholder={hasOnlyOneOption ? "" : "bitte auswählen"}
                                        {...field}
                                        value={
                                          hasOnlyOneOption && singleOptionValue
                                            ? singleOptionValue
                                            : field.value ?? fieldValues.defaultValue
                                        }
                                        onChange={(e) => {
                                          if (isDNField) setSelectedDNFieldValue(e.target.value)
                                          if (isPressureFieldWithOverpressure)
                                            setSelectedPressureFieldValue(e.target.value)
                                          field.onChange(e)
                                        }}
                                        isDisabled={isFieldDisabled || hasOnlyOneOption}
                                      >
                                        {fieldValues.selectValues?.map((label, index) => {
                                          // When optionKeys is provided (parallel to selectValues), submit the key but show the label.
                                          const submitValue = fieldValues.optionKeys?.[index] ?? label
                                          return (
                                            <option key={submitValue + String(index)} value={submitValue}>
                                              {fieldValues.unit === 'm'
                                                ? Number(label) / 1000
                                                : label}
                                            </option>
                                          )
                                        })}
                                      </Select>
                                      <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                                      {isDNField &&
                                        selectedPressureFieldValue === 'Überdruck' &&
                                        (selectedDNFieldValue === '100' ||
                                          selectedDNFieldValue === '80') && (
                                          <Text pt={2} fontSize="sm">
                                            {formatMessage({ id: '--dn-note' })}
                                          </Text>
                                        )}
                                      {isFixationRopeSetField &&
                                        (selectedDNFieldValue === '100' ||
                                          selectedDNFieldValue === '80') && (
                                          <Text pt={2} fontSize="sm">
                                            {formatMessage({ id: '--fixation-rop-set-dn-note' })}
                                          </Text>
                                        )}
                                    </FormControl>
                                  )
                                }}
                              </Field>
                            )

                            switch (fieldValues.valueType) {
                              case 'NUMBER':
                                return (
                                  <Box key={fieldValues.name + fieldValues.id}>{numberField}</Box>
                                )
                              case 'BOOL':
                                return (
                                  <Box key={fieldValues.name + fieldValues.id}>{checkBoxField}</Box>
                                )
                              case 'SELECT':
                                return (
                                  <Box key={fieldValues.name + fieldValues.id}>{selectField}</Box>
                                )
                              case 'DYNAMIC_SELECT':
                                if (fieldValues.selectValues?.length === 0) {
                                  /* (
                                    <Alert status="info">
                                      <AlertIcon/>
                                      {fieldValues.name} hat keine Werte für diese Konfiguration und wird übersprungen
                                    </Alert>
                                  ) */
                                  return
                                }
                                return (
                                  <Box key={fieldValues.name + fieldValues.id}>{selectField}</Box>
                                )
                              default:
                                return undefined
                            }
                          })}
                          <HStack justify="space-between">
                            <Text
                              fontSize="sm"
                              color="var(--chakra-colors-red-500)"
                              w="max-content"
                            >
                              {formatMessage({ id: '--required-field' })}
                            </Text>
                            <Button
                              type="submit"
                              w="fit-content"
                              colorScheme="accent"
                              isLoading={isBusy}
                              isDisabled={!props.isValid}
                            >
                              {index === accordionItems.length - 1
                                ? formatMessage({ id: '--calculate' })
                                : formatMessage({ id: '--next' })}
                            </Button>
                          </HStack>
                        </Stack>
                        </Box>
                      </Form>
                      )
                    }}
                  </Formik>
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          </Fragment>
        ))}
    </Accordion>
  )
}
